// frontend/src/components/SearchFiles.tsx - v3.8
import React, { useState, useCallback, useMemo, useRef, useEffect } from 'react';
import {
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  CircularProgress,
  Alert,
  Snackbar,
  Tooltip,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  Button,
  Paper,
  Fade
} from '@mui/material';
import {
  GetApp as DownloadIcon,
  PlayArrow as PlayIcon,
  MoreVert as MoreVertIcon,
  Link as LinkIcon,
  InsertDriveFile as FileIcon,
  VideoFile as VideoIcon,
  Description as TextIcon,
  TableChart as CsvIcon,
  AudioFile as AudioIcon,
  Image as ImageIcon,
  PictureAsPdf as PdfIcon,
  ArrowDownward as ArrowDownwardIcon,
  ArrowUpward as ArrowUpwardIcon,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import {
  searchAllFiles,
  getPresignedDownloadUrl,
  FileMetadata,
  FileType,
  PresignedUrlResponse,
  formatFileSize,
  getFileName
} from '../services/api';
import { getDisplayFileName, getFileTypeFromExtension } from '../services/FileUtils';
import { recordActivity } from '../services/activityService';
import SearchBar, { SearchParams } from '../components/SearchBar';

const SearchFiles: React.FC = () => {
  const [files, setFiles] = useState<FileMetadata[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [downloadLoading, setDownloadLoading] = useState<string | null>(null);
  const [searched, setSearched] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [snackbarOpen, setSnackbarOpen] = useState<boolean>(false);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [selectedFile, setSelectedFile] = useState<FileMetadata | null>(null);
  const [sortConfig, setSortConfig] = useState<{
    key: string;
    direction: 'asc' | 'desc';
  }>({
    key: 'name',
    direction: 'asc'
  });
  const [visibleFiles, setVisibleFiles] = useState(25); // Initial limit of 25 files
  const [searchText, setSearchText] = useState<string>(''); // Track current search text for filtering
  const observer = useRef<IntersectionObserver | null>(null);
  const loadMoreRef = useRef<HTMLDivElement>(null);
  const navigate = useNavigate();

  const sortedFiles = useMemo(() => {
    if (!Array.isArray(files)) {
      console.error('files is not an array:', files);
      return [];
    }
   
    const sorted = [...files];
    return sorted.sort((a, b) => {
      let comparison = 0;
      switch (sortConfig.key) {
        case 'size':
          comparison = a.size - b.size;
          break;
        case 'date':
          const dateA = a.lastModified ? new Date(a.lastModified).getTime() : 0;
          const dateB = b.lastModified ? new Date(b.lastModified).getTime() : 0;
          comparison = dateA - dateB;
          break;
        case 'name':
        default:
          const fileNameA = getDisplayFileName(a.key);
          const fileNameB = getDisplayFileName(b.key);
          comparison = fileNameA.toLowerCase().localeCompare(fileNameB.toLowerCase());
          break;
      }
      return sortConfig.direction === 'desc' ? -comparison : comparison;
    });
  }, [files, sortConfig]);

  const filteredFiles = useMemo(() => {
    // Client-side filter strictly on base filename (excluding path and parent folders)
    return sortedFiles.filter(file => (file.key.split('/').pop() || '').toLowerCase().includes(searchText.toLowerCase()));
  }, [sortedFiles, searchText]);

  useEffect(() => {
    if (observer.current) observer.current.disconnect();
    observer.current = new IntersectionObserver(entries => {
      if (entries[0].isIntersecting && visibleFiles < filteredFiles.length) {
        setVisibleFiles(prev => Math.min(prev + 25, filteredFiles.length));
      }
    });
    if (loadMoreRef.current) observer.current.observe(loadMoreRef.current);
    return () => { if (observer.current) observer.current.disconnect(); };
  }, [filteredFiles, visibleFiles]);

  const formatDate = useCallback((dateString: string | null): string => {
    if (!dateString) return 'N/A';
    try {
      const date = new Date(dateString);
      if (isNaN(date.getTime())) return 'N/A';
      const now = new Date();
      const isThisYear = now.getFullYear() === date.getFullYear();
      return isThisYear
        ? `${date.toLocaleString('en-US', { month: 'short' })} ${date.getDate()}`
        : `${date.toLocaleString('en-US', { month: 'short' })} ${date.getDate()}, ${date.getFullYear()}`;
    } catch (error) {
      console.error('Error formatting date:', error);
      return 'N/A';
    }
  }, []);

  const getFileIcon = useCallback((file: FileMetadata) => {
    if (file.fileType) {
      switch (file.fileType) {
        case FileType.VIDEO: return <VideoIcon color="primary" />;
        case FileType.CSV: return <CsvIcon color="success" />;
        case FileType.TEXT: return <TextIcon color="info" />;
        case FileType.AUDIO: return <AudioIcon color="secondary" />;
        case FileType.IMAGE: return <ImageIcon color="action" />;
        case FileType.DOCUMENT: return <PdfIcon color="warning" />;
        default: return <FileIcon color="primary" />;
      }
    }
    const extension = file.key.split('.').pop()?.toLowerCase();
    if (['mp4', 'mov', 'webm'].includes(extension || '')) return <VideoIcon color="primary" />;
    if (extension === 'csv') return <CsvIcon color="success" />;
    if (['txt', 'log', 'opus'].includes(extension || '')) return <TextIcon color="info" />;
    if (['mp3', 'wav', 'ogg'].includes(extension || '')) return <AudioIcon color="secondary" />;
    if (['jpg', 'jpeg', 'png', 'gif'].includes(extension || '')) return <ImageIcon color="action" />;
    if (['pdf', 'doc', 'docx'].includes(extension || '')) return <PdfIcon color="warning" />;
    return <FileIcon color="primary" />;
  }, []);

  const isFileViewable = useCallback((file: FileMetadata): boolean => {
    if (typeof file.isViewable === 'boolean') return file.isViewable;
    const fileType = file.fileType || '';
    return [FileType.VIDEO, FileType.CSV, FileType.TEXT].includes(fileType as FileType);
  }, []);

  const isVideoFile = useCallback((file: FileMetadata): boolean => {
    if (file.fileType === FileType.VIDEO) return true;
    const extension = file.key.split('.').pop()?.toLowerCase();
    return ['mp4', 'mov', 'webm'].includes(extension || '');
  }, []);

  const handleFileClick = useCallback(async (file: FileMetadata) => {
    const fileType = file.fileType || getFileTypeFromExtension(file.key);
    if (fileType === FileType.CSV) {
      recordActivity(file.key, 'viewed', FileType.CSV);
      navigate(`/view/csv/${encodeURIComponent(file.key)}`);
    } else if (fileType === FileType.TEXT) {
      recordActivity(file.key, 'viewed', FileType.TEXT);
      navigate(`/view/text/${encodeURIComponent(file.key)}`);
    } else if (fileType === FileType.IMAGE) {
      recordActivity(file.key, 'viewed', FileType.IMAGE);
      navigate(`/view/image/${encodeURIComponent(file.key)}`);
    } else if (fileType === FileType.VIDEO || file.isViewable) {
      recordActivity(file.key, 'played', FileType.VIDEO);
      navigate(`/play/${encodeURIComponent(file.key)}`);
    } else {
      try {
        recordActivity(file.key, 'downloaded', fileType as any);
        const response = await getPresignedDownloadUrl(file.key);
        window.open(response.url, '_blank');
      } catch (err) {
        console.error('Error opening file:', err);
        setError('Failed to open file. Please try downloading it manually.');
        setSnackbarOpen(true);
      }
    }
  }, [navigate]);

  const handleSortChange = useCallback((key: string, direction: 'asc' | 'desc') => {
    setSortConfig({ key, direction });
  }, []);

  const handleDownload = useCallback(async (event: React.MouseEvent<HTMLLIElement>, key: string) => {
    event.preventDefault();
    event.stopPropagation();
    recordActivity(key, 'downloaded', getFileTypeFromExtension(key));
    setDownloadLoading(key);
    try {
      console.log('[Debug] Download key:', key, 'Display name:', getDisplayFileName(key));
      const response: PresignedUrlResponse = await getPresignedDownloadUrl(key);
      const link = document.createElement('a');
      link.href = response.url;
      link.download = response.filename || getFileName(key); // Backward compatibility with original name
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } catch (err) {
      console.error('Error downloading file:', { key, error: err });
      setError('Failed to download file. Please try again.');
      setSnackbarOpen(true);
    } finally {
      setDownloadLoading(null);
    }
    handleMenuClose();
  }, []);

  const handlePlay = useCallback((event: React.MouseEvent<HTMLLIElement>, key: string) => {
    event.preventDefault();
    event.stopPropagation();
    console.log('[Debug] Playing file:', key, 'Display name:', getDisplayFileName(key));
    recordActivity(key, 'played', getFileTypeFromExtension(key));
    navigate(`/play/${encodeURIComponent(key)}`);
    handleMenuClose();
  }, [navigate]);

  const handleCopyLink = useCallback(async (event: React.MouseEvent<HTMLLIElement>, file: FileMetadata) => {
    event.preventDefault();
    event.stopPropagation();
    const fileType = file.fileType || '';
    let url = '';
   
    const origin = window.location.origin;
   
    try {
      if (fileType === FileType.CSV) {
        url = `${origin}/view/csv/${encodeURIComponent(file.key)}`;
      } else if (fileType === FileType.TEXT) {
        url = `${origin}/view/text/${encodeURIComponent(file.key)}`;
      } else if (fileType === FileType.VIDEO || file.isViewable) {
        url = `${origin}/play/${encodeURIComponent(file.key)}`;
      } else {
        const response = await getPresignedDownloadUrl(file.key);
        url = response.url;
      }
     
      await navigator.clipboard.writeText(url);
      console.log('URL copied to clipboard:', url);
      setError('Link copied to clipboard');
      setSnackbarOpen(true);
    } catch (err) {
      console.error('Failed to copy URL:', err);
      const textarea = document.createElement('textarea');
      textarea.value = url;
      document.body.appendChild(textarea);
      textarea.select();
      try {
        document.execCommand('copy');
        setError('Link copied to clipboard');
        setSnackbarOpen(true);
      } catch (e) {
        console.error('Fallback copy failed:', e);
        setError('Failed to copy link. Please copy it manually: ' + url);
        setSnackbarOpen(true);
      }
      document.body.removeChild(textarea);
    }
   
    handleMenuClose();
  }, []);

  const handleSearch = useCallback((params: SearchParams) => {
    console.log('Search triggered with params:', params);
    setSortConfig({
      key: params.sortBy || 'name',
      direction: (params.sortDirection || 'asc') as 'asc' | 'desc'
    });
    setSearchText(params.searchText || '');
    setVisibleFiles(25); // Reset to 25 on new search
   
    if (!params.searchText) {
      setFiles([]);
      setSearched(false);
      setError(null);
      return;
    }
   
    setLoading(true);
    setSearched(true);
    recordActivity(params.searchText, 'searched', null, params.searchText);
   
    searchAllFiles(params.searchText, params.sortBy || 'name', params.sortDirection || 'asc')
      .then((results) => {
        console.log('Search results:', results.files); // Debug log
        setFiles(results.files || []);
        setError(null);
      })
      .catch((err) => {
        console.error('Error searching files:', err.message || (err.stack ? err.stack : 'Unknown error'));
        setError('Failed to search files. Please try again later. Error: ' + (err.message || (err.stack ? err.stack : 'Unknown')));
        setSnackbarOpen(true);
      })
      .finally(() => {
        setLoading(false);
      });
  }, []);

  const handleActionClick = (event: React.MouseEvent<HTMLButtonElement>, file: FileMetadata) => {
    event.preventDefault();
    event.stopPropagation();
    setAnchorEl(event.currentTarget);
    setSelectedFile(file);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
    setSelectedFile(null);
  };

  const handleSnackbarClose = useCallback(() => {
    setSnackbarOpen(false);
  }, []);

  return (
    <Box sx={{ maxWidth: 1200, mx: 'auto', p: 2 }}>
      <Typography variant="h4" gutterBottom>
        Search Files
      </Typography>
     
      <SearchBar onSearch={handleSearch} />
     
      {loading ? (
        <Fade in={loading} timeout={300}>
          <Box sx={{ display: 'flex', justifyContent: 'center', my: 4 }}>
            <CircularProgress />
          </Box>
        </Fade>
      ) : error ? (
        <Fade in={Boolean(error)} timeout={300}>
          <Alert
            severity="error"
            action={
              <Button
                color="inherit"
                size="small"
                onClick={() => handleSearch({
                  searchText: searchText,
                  sortBy: sortConfig.key,
                  sortDirection: sortConfig.direction
                })}
                disabled={loading}
              >
                {loading ? <CircularProgress size={20} /> : 'Retry'}
              </Button>
            }
          >
            {error}
          </Alert>
        </Fade>
      ) : searched && filteredFiles.length === 0 ? (
        <Fade in={searched && filteredFiles.length === 0} timeout={300}>
          <Alert severity="info">No files found matching your search criteria.</Alert>
        </Fade>
      ) : filteredFiles.length > 0 && (
        <Fade in={!loading && filteredFiles.length > 0} timeout={500}>
          <Box>
            <TableContainer component={Paper}>
              <Table sx={{ minWidth: 650 }} aria-label="search results table">
                <TableHead>
                  <TableRow sx={{ backgroundColor: '#f5f5f5' }}>
                    <TableCell
                      sx={{
                        fontWeight: 'bold',
                        cursor: 'pointer',
                        minWidth: '400px',
                        '&:hover': { backgroundColor: '#e0e0e0' }
                      }}
                      onClick={() => handleSortChange('name', sortConfig.key === 'name' && sortConfig.direction === 'asc' ? 'desc' : 'asc')}
                    >
                      <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        Name
                        {sortConfig.key === 'name' && (
                          sortConfig.direction === 'asc'
                            ? <ArrowDownwardIcon fontSize="small" sx={{ ml: 0.5 }} />
                            : <ArrowUpwardIcon fontSize="small" sx={{ ml: 0.5 }} />
                        )}
                      </Box>
                    </TableCell>
                    <TableCell
                      sx={{
                        fontWeight: 'bold',
                        cursor: 'pointer',
                        minWidth: '150px',
                        '&:hover': { backgroundColor: '#e0e0e0' }
                      }}
                      onClick={() => handleSortChange('date', sortConfig.key === 'date' && sortConfig.direction === 'asc' ? 'desc' : 'asc')}
                      align="right"
                    >
                      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end' }}>
                        Last Modified
                        {sortConfig.key === 'date' && (
                          sortConfig.direction === 'asc'
                            ? <ArrowDownwardIcon fontSize="small" sx={{ ml: 0.5 }} />
                            : <ArrowUpwardIcon fontSize="small" sx={{ ml: 0.5 }} />
                        )}
                      </Box>
                    </TableCell>
                    <TableCell
                      sx={{
                        fontWeight: 'bold',
                        cursor: 'pointer',
                        minWidth: '100px',
                        '&:hover': { backgroundColor: '#e0e0e0' }
                      }}
                      onClick={() => handleSortChange('size', sortConfig.key === 'size' && sortConfig.direction === 'asc' ? 'desc' : 'asc')}
                      align="right"
                    >
                      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end' }}>
                        File Size
                        {sortConfig.key === 'size' && (
                          sortConfig.direction === 'asc'
                            ? <ArrowDownwardIcon fontSize="small" sx={{ ml: 0.5 }} />
                            : <ArrowUpwardIcon fontSize="small" sx={{ ml: 0.5 }} />
                        )}
                      </Box>
                    </TableCell>
                    <TableCell align="center" sx={{ width: '80px' }}>
                      <MoreVertIcon fontSize="small" />
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {filteredFiles.slice(0, visibleFiles).map((file) => (
                    <TableRow
                      key={file.key}
                      sx={{ '&:hover': { backgroundColor: '#f5f5f5' } }}
                      onClick={() => handleFileClick(file)}
                      style={{ cursor: isFileViewable(file) ? 'pointer' : 'default' }}
                    >
                      <TableCell component="th" scope="row">
                        <Box sx={{ display: 'flex', alignItems: 'center' }}>
                          {getFileIcon(file)}
                          <Tooltip
                            title={isFileViewable(file)
                              ? `Click to view ${getDisplayFileName(file.key)}${file.key.includes('ProcessLog.csv') ? ' (Process Log)' : ''}`
                              : `Click to download ${getDisplayFileName(file.key)} (Not viewable in browser)`
                            }
                          >
                            <Typography sx={{ ml: 2, color: 'text.primary' }}>
                              {getDisplayFileName(file.key)}
                            </Typography>
                          </Tooltip>
                        </Box>
                      </TableCell>
                      <TableCell align="right">
                        {formatDate(file.lastModified)}
                      </TableCell>
                      <TableCell align="right">{formatFileSize(file.size)}</TableCell>
                      <TableCell align="center">
                        {downloadLoading === file.key ? (
                          <CircularProgress size={20} />
                        ) : (
                          <IconButton
                            size="small"
                            onClick={(e) => handleActionClick(e, file)}
                            aria-label={`Actions for ${getDisplayFileName(file.key)}`}
                          >
                            <MoreVertIcon fontSize="small" />
                          </IconButton>
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
            {visibleFiles < filteredFiles.length && (
              <Box ref={loadMoreRef} sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
                <Button variant="contained" onClick={() => setVisibleFiles(prev => Math.min(prev + 25, filteredFiles.length))}>Load More</Button>
              </Box>
            )}
          </Box>
        </Fade>
      )}
     
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleMenuClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        {selectedFile && (
          <>
            {/* Only show Play option for video files */}
            {isVideoFile(selectedFile) && (
              <MenuItem onClick={(e) => handlePlay(e, selectedFile.key)} disabled={downloadLoading === selectedFile.key}>
                <ListItemIcon>
                  <PlayIcon fontSize="small" />
                </ListItemIcon>
                <ListItemText>Play</ListItemText>
              </MenuItem>
            )}
            <MenuItem onClick={(e) => handleDownload(e, selectedFile.key)} disabled={downloadLoading === selectedFile.key}>
              <ListItemIcon>
                <DownloadIcon fontSize="small" />
              </ListItemIcon>
              <ListItemText>Download</ListItemText>
            </MenuItem>
            <MenuItem onClick={(e) => handleCopyLink(e, selectedFile)}>
              <ListItemIcon>
                <LinkIcon fontSize="small" />
              </ListItemIcon>
              <ListItemText>Copy Link</ListItemText>
            </MenuItem>
          </>
        )}
      </Menu>
     
      <Snackbar
        open={snackbarOpen}
        autoHideDuration={6000}
        onClose={handleSnackbarClose}
        message={error}
      />
    </Box>
  );
};

export default SearchFiles;